/**
 * Deep-strips `undefined` from a value before a Firestore write.
 *
 * Firestore's SDK rejects `undefined` unless `getFirestore` is configured with
 * `ignoreUndefinedProperties` (this app's call, `frontend/src/firebase.js`,
 * does not set it) — and even then it would drop the key, not keep a hole. A
 * `FirestoreDataConverter`'s `toFirestore` (e.g. `themeV2Converter.ts`) only
 * strips `undefined` at the TOP LEVEL of the object it is handed
 * (`Object.keys(result).forEach(...)`); it does not recurse. A NESTED
 * `undefined` — e.g. a theme's `animationSettings.transition.direction` reset
 * to "Default", which `structuredClone` keeps as an own key — reaches the SDK
 * unstripped and throws synchronously, before `commit()` (interim review I-1).
 *
 * Originally private to `ProjectThemeService` (its only caller before this
 * module existed); moved here so `ThemeV2Service.update` can pass it too as
 * `mergeWriteReplacingFields`'s `prepareData`, giving both services the same
 * protection regardless of whether their reference is converter-bound.
 *
 * @module firebase-core/utils
 */

/**
 * Recursively removes `undefined` from `value`:
 * - an object's own key is DROPPED when its value is `undefined` (never kept
 *   as `null` — Firestore's `merge: true` only ever adds/overwrites a key
 *   actually present in the new value, so a dropped key behaves exactly like
 *   one the caller never touched);
 * - an array's `undefined` ITEM becomes `null` instead (arrays have no holes;
 *   dropping an index would shift every later element);
 * - every other value (including `null`, primitives, and the top-level value
 *   itself when it is `undefined`) passes through unchanged, except that a
 *   literal `undefined` passed as `value` itself returns `null`.
 *
 * @warning Plain JSON-like data only. A `Date`, a Firestore `Timestamp`, a
 * `FieldValue` sentinel (`serverTimestamp()`, `arrayUnion()`, …) or any other
 * class instance has no OWN enumerable data keys, so the `Object.entries` walk
 * below sees `{}` and flattens the whole value to an empty object. Never route
 * such a value through this helper. Today that holds: `prepareData` runs
 * BEFORE the converter creates any `Timestamp`, and `updatedAt` is written as
 * a plain ISO string, not a `FieldValue`.
 */
export function stripUndefined<T>(value: T): T {
  if (value === undefined) {
    return null as T;
  }

  if (Array.isArray(value)) {
    return value.map((item) => stripUndefined(item)) as T;
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>(
    (result, [key, child]) => {
      if (child !== undefined) {
        result[key] = stripUndefined(child);
      }
      return result;
    },
    {}
  ) as T;
}
